import { useNavigate, useLocation } from 'react-router-dom';
import { TrendingUp, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { USER_NAV_SECTIONS, UserNavItem } from '../../config/userNavigation';
import { usePackAccess } from '../../hooks/usePackAccess';
import { useSubscriptionAccess } from '../../hooks/useSubscriptionAccess';
import { usePermissions } from '../../hooks/usePermissions';
import { useSupportNotifications } from '../../hooks/useSupportNotifications';
import { useAccountNotification } from '../../hooks/useAccountNotification';
import { Badge } from '../ui/Badge';

interface UserSidebarProps {
  collapsed: boolean;
  onCollapse: (value: boolean) => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export function UserSidebar({ collapsed, onCollapse, mobileOpen, onMobileClose }: UserSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAccess, canAccessReports, canAccessDafSpace } = usePackAccess();
  const { canAccessBudget } = useSubscriptionAccess();
  const { permissions } = usePermissions();
  const { unreadCount } = useSupportNotifications();
  const { showNotification } = useAccountNotification();

  const isActive = (path: string) => {
    if (path === '/app' && location.pathname === '/app') return true;
    if (path !== '/app' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const canAccessItem = (item: UserNavItem): boolean => {
    if (!item.requiresAccess) return true;

    switch (item.requiresAccess) {
      case 'budget':
        return canAccessBudget;
      case 'security':
        return true;
      case 'associations':
        return permissions?.can_manage_subventions || false;
      case 'modules':
        return hasAccess('modules_metiers');
      case 'reports':
        return canAccessReports;
      case 'daf':
        return canAccessDafSpace;
      case 'pme':
        return permissions?.can_manage_pme || false;
      case 'agri':
        return permissions?.can_manage_agri || false;
      default:
        return false;
    }
  };

  const handleItemClick = (item: UserNavItem) => {
    navigate(item.path);
    onMobileClose();
  };

  const getNotificationBadge = (itemId: string) => {
    if (itemId === 'support' && unreadCount > 0) {
      return unreadCount;
    }
    if (itemId === 'settings' && showNotification) {
      return '!';
    }
    return null;
  };

  const sidebarContent = (isMobile = false) => (
    <>
      <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
        {(!collapsed || isMobile) && (
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center">
              <TrendingUp className="text-white" size={20} />
            </div>
            <div>
              <span className="text-lg font-bold text-gray-900">TrezoOne</span>
            </div>
          </div>
        )}
        {collapsed && !isMobile && (
          <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center mx-auto">
            <TrendingUp className="text-white" size={20} />
          </div>
        )}
        {isMobile && (
          <button
            onClick={onMobileClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
          >
            <X size={20} />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto py-4 px-3">
        <nav className="space-y-6">
          {USER_NAV_SECTIONS.map((section) => {
            const visibleItems = section.items.filter(item => canAccessItem(item));

            if (visibleItems.length === 0) return null;

            return (
              <div key={section.id}>
                {(!collapsed || isMobile) && (
                  <p className="px-3 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {section.label}
                  </p>
                )}
                {collapsed && !isMobile && (
                  <div className="h-px bg-gray-200 mb-2"></div>
                )}
                <div className="space-y-1">
                  {visibleItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    const notificationCount = getNotificationBadge(item.id);

                    return (
                      <button
                        key={item.id}
                        onClick={() => handleItemClick(item)}
                        className={`
                          w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                          transition-all duration-150 group relative
                          ${active
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-700 hover:bg-gray-50'
                          }
                        `}
                        title={collapsed && !isMobile ? item.label : undefined}
                      >
                        <Icon size={20} className="flex-shrink-0" />
                        {(!collapsed || isMobile) && (
                          <>
                            <span className="flex-1 text-left">{item.label}</span>
                            {notificationCount && (
                              <Badge variant="danger" size="sm">
                                {notificationCount}
                              </Badge>
                            )}
                          </>
                        )}
                        {collapsed && !isMobile && notificationCount && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center">
                            <span className="text-xs font-bold text-white">{notificationCount}</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>
      </div>

      {!isMobile && (
        <div className="border-t border-gray-200 p-3">
          <button
            onClick={() => onCollapse(!collapsed)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
            title={collapsed ? 'Agrandir' : 'Réduire'}
          >
            {collapsed ? (
              <ChevronRight size={20} />
            ) : (
              <>
                <ChevronLeft size={20} />
                <span>Réduire</span>
              </>
            )}
          </button>
        </div>
      )}
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`
          hidden md:flex fixed top-0 left-0 h-screen bg-white border-r border-gray-200
          transition-all duration-300 z-30 flex-col
          ${collapsed ? 'w-20' : 'w-64'}
        `}
      >
        {sidebarContent(false)}
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Mobile drawer */}
      <aside
        className={`
          fixed top-0 left-0 h-screen w-72 bg-white border-r border-gray-200
          z-50 flex flex-col md:hidden
          transition-transform duration-300
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {sidebarContent(true)}
      </aside>
    </>
  );
}
